<script lang="ts">
    import CN from "classnames";

    import type { TextFontWeight, TextTone, TextVariant } from "./types";

    type Component = "p" | "span" | "h1" | "h2" | "h3" | "h4";
    type TextAlignment = "start" | "center" | "end" | "justify";
    type TextDecoration = "underline";
    type WordBreak = "break-word" | "break-all";

    export let component: Component = "p";
    export let variant: TextVariant = "body-lg";
    export let tone: TextTone = "base";
    export let fontWeight: TextFontWeight | null = null;
    export let alignment: TextAlignment | null = null;
    export let textDecoration: TextDecoration | null = null;
    export let lineClamp: number | null = null;
    export let wordBreak: WordBreak | null = null;
    export let visuallyHidden = false;
</script>

<svelte:element
    this={component}
    class={CN(
        "gw-text",
        visuallyHidden
            ? "gw-text--visually-hidden"
            : [
                  `gw-text--${variant}`,
                  `gw-text--tone-${tone}`,
                  fontWeight && `gw-text--${fontWeight}`,
                  alignment && `gw-text--align-${alignment}`,
                  textDecoration && "gw-text--underline",
                  lineClamp && "gw-text--line-clamp",
                  wordBreak && `gw-text--${wordBreak}`,
              ]
    )}
    style:--gw-line-clamp={lineClamp}
>
    <slot />
</svelte:element>

<style lang="postcss">
    @define-mixin variant $name {
        font-size: var(--gw-text-$(name)-font-size);
        line-height: var(--gw-text-$(name)-font-line-height);
        font-weight: var(--gw-text-$(name)-font-weight);
    }

    @define-mixin mobile-query $font-size, $line-height {
        @media screen and (max-width: 768px) {
            font-size: $font-size;
            line-height: $line-height;
        }
    }

    .gw-text {
        margin: 0;
        text-align: inherit;
        letter-spacing: var(--gw-size-0);

        &--heading-3xl {
            @mixin variant heading-3xl;

            @mixin mobile-query var(--gw-size-900), var(--gw-size-1200);
        }

        &--heading-2xl {
            @mixin variant heading-2xl;

            @mixin mobile-query var(--gw-size-750), var(--gw-size-1000);
        }

        &--heading-xl {
            @mixin variant heading-xl;

            @mixin mobile-query var(--gw-size-600), var(--gw-size-800);
        }

        &--heading-lg {
            @mixin variant heading-lg;
        }

        &--heading-md {
            @mixin variant heading-md;
        }

        &--heading-sm {
            @mixin variant heading-sm;
        }

        &--body-xlg {
            @mixin variant body-xlg;
        }

        &--body-lg {
            @mixin variant body-lg;
        }

        &--body-md {
            @mixin variant body-md;
        }

        &--body-sm {
            @mixin variant body-sm;
        }

        &--body-xs {
            @mixin variant body-xs;
        }

        &--body-xxs {
            @mixin variant body-xxs;
        }

        &--underline {
            text-decoration: underline;
        }

        &--tone-base {
            color: var(--gw-text-primary);
        }

        &--tone-subdued {
            color: var(--gw-text-secondary);
        }

        &--tone-critical {
            color: var(--gw-text-critical);
        }

        &--tone-success {
            color: var(--gw-icon-success);
        }

        &--tone-tertiary {
            color: var(--gw-text-tertiary);
        }

        &--tone-disabled {
            color: var(--gw-text-primary-disabled);
        }

        &--tone-emphasis {
            color: var(--gw-text-emphasis);
        }

        &--tone-inherit {
            color: inherit;
        }

        &--regular {
            font-weight: var(--gw-weight-450);
        }

        &--medium {
            font-weight: var(--gw-weight-550);
        }

        &--semibold {
            font-weight: var(--gw-weight-650);
        }

        &--bold {
            font-weight: var(--gw-weight-700);
        }

        &--align-start {
            text-align: start;
        }

        &--align-center {
            text-align: center;
        }

        &--align-end {
            text-align: end;
        }

        &--align-justify {
            text-align: justify;
        }

        &--break-word {
            word-break: break-word;
        }

        &--break-all {
            word-break: break-all;
        }

        &--line-clamp {
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-line-clamp: var(--gw-line-clamp);
            -webkit-box-orient: vertical;
        }

        &--word-break {
            word-break: var(--gw-word-break);
        }

        &--visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: 0;
            overflow: hidden;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            white-space: nowrap;
            border: 0;
        }
    }
</style>
